/**
 * Reclamações do Mercado Livre (SAC): mediações, devoluções, trocas e cancelamentos. As que precisam de ação do
 * vendedor ficam no topo, pelo prazo do ML. Ao lado, o que o ML diz da reclamação, se afeta a reputação (resolver
 * nas primeiras 48 h com incentivo não afeta), o que o comprador quer, a devolução ou a troca, as ações que o ML
 * libera agora e as mensagens (com o comprador; em mediação, só com o mediador).
 * Devolver dinheiro (total ou parcial) só com perfil admin. Abrir pelo pedido: /canal/mercadolivre/reclamacoes?order=<id>.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Gavel, Package, Paperclip, RefreshCw, Repeat, Search, Send, ShieldAlert, X } from 'lucide-react';
import { api, brl, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { Head, Pager, useSearchBox, useUrlState } from './MeliChannel.tsx';
import { ago } from './Questions.tsx';

interface OrderSummary { id: string; externalId: string; packId: string | null; buyer: string | null; status: string; shipmentStatus: string | null; logisticType: string | null; total: number | null; items: string | null; at: string | null }
interface Claim {
  id: string; externalId: string; account: string | null; type: string; typeLabel: string; stage: string; stageLabel: string; status: string;
  reasonId: string | null; reasonGroup: string | null; reasonDetail: string | null; title: string | null; description: string | null; problem: string | null;
  responsible: string | null; due: string | null; affectsReputation: string | null; hasIncentive: boolean | null; reputationDue: string | null;
  openedAt: string | null; closedAt: string | null; messages: number; unread: number; lastMessageAt: string | null;
  returnStatus: string | null; returnStatusLabel: string | null; hasChange: boolean;
  resolution: { reason: string; label: string; benefited: string[]; closedBy: string | null; coverage: boolean } | null;
  order: OrderSummary | null; syncError: string | null;
}
interface ListPage { accounts: { id: string; name: string }[]; counts: { action: number; urgent: number; open: number; dispute: number; returns: number; affects: number }; total: number; page: number; pageSize: number; rows: Claim[] }
type ActionKey = 'refund' | 'partial_refund' | 'allow_return' | 'allow_replace' | 'open_dispute' | 'shipping_evidence' | 'shipping_promise' | 'return_review_ok' | 'return_review_fail';
interface Detail extends Omit<Claim, 'messages'> {
  fulfilled: boolean | null; quantityType: string | null; claimedQuantity: number | null;
  actions: { action: string; label: string; mandatory: boolean; due: string | null }[];
  available: { action: ActionKey; needsAdmin: boolean; allowed: boolean }[];
  receivers: ('complainant' | 'mediator')[];
  expected: { who: string; resolution: string; label: string; status: string; details: Record<string, string>; at: string | null }[];
  returnInfo: {
    id: number; status: string; statusLabel: string; subtype: string | null; money: string | null; moneyLabel: string | null; refundAt: string | null; cost: number | null; closedAt: string | null;
    shipments: { id: string; status: string; tracking: string | null; destination: string | null; type: string }[];
    orders: { orderId: string; itemId: string; quantity: number }[];
    reviews: Record<string, string | number | null>[];
  } | null;
  change: { type: string; status: string; statusLabel: string; statusDetail: string | null; newOrders: string[]; estimated: { from?: string; to?: string } | null; items: { id: string; quantity: number; price: number }[] } | null;
  evidences: { type: string; method: string | null; company: string | null; tracking: string | null; dateShipped: string | null; dateDelivered: string | null; handlingDate: string | null; attachments: { filename: string; name: string }[] }[];
  messages: { id: string; from: string; to: string | null; text: string; status: string | null; stage: string | null; moderation: { status: string; reason: string | null } | null; attachments: { filename: string; name: string }[]; sentAt: string; readAt: string | null; sentBy: string | null }[];
}

const ACTION_TEXT: Record<ActionKey, { label: string; hint: string }> = {
  allow_return: { label: 'Aceitar a devolução', hint: 'O Mercado Livre gera a etiqueta para o comprador devolver. O dinheiro volta a ele quando a devolução for despachada ou entregue.' },
  refund: { label: 'Devolver o dinheiro', hint: 'Reembolso total ao comprador, sem devolução do produto. Encerra a reclamação.' },
  partial_refund: { label: 'Oferecer reembolso parcial', hint: 'O comprador fica com o produto e recebe parte do valor, se aceitar.' },
  allow_replace: { label: 'Oferecer troca pelo mesmo produto', hint: 'O comprador recebe outro igual (Full).' },
  open_dispute: { label: 'Pedir mediação', hint: 'Um representante do Mercado Livre entra no caso. Depois disso as mensagens vão só para o mediador.' },
  shipping_evidence: { label: 'Informar prova de envio', hint: 'Para envio feito pela loja (correio, transportadora, em mãos ou e-mail).' },
  shipping_promise: { label: 'Informar data prevista de envio', hint: 'Quando o produto ainda não saiu.' },
  return_review_ok: { label: 'A devolução chegou bem', hint: 'Confirma que o produto devolvido chegou como esperado.' },
  return_review_fail: { label: 'Problema na devolução', hint: 'O produto voltou danificado, incompleto, diferente ou não chegou.' },
};
const SHIP: Record<string, string> = { pending: 'pendente', handling: 'preparando', ready_to_ship: 'pronto para enviar', shipped: 'a caminho', delivered: 'entregue', not_delivered: 'não entregue', cancelled: 'cancelado' };
const ROLE: Record<string, string> = { complainant: 'comprador', respondent: 'você', mediator: 'mediador do ML' };
const WHO: Record<string, string> = { seller: 'A vez é sua', buyer: 'Aguardando o comprador', mediator: 'Com o mediador do Mercado Livre' };

const until = (iso: string | null) => {
  if (!iso) return null;
  const m = Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
  if (m <= 0) return 'prazo vencido';
  return m < 60 ? `faltam ${m} min` : m < 2880 ? `faltam ${Math.floor(m / 60)} h` : `faltam ${Math.floor(m / 1440)} dias`;
};
const urgent = (iso: string | null) => !!iso && new Date(iso).getTime() - Date.now() < 24 * 3_600_000;
const toBase64 = async (f: File) => ({
  filename: f.name, contentType: f.type || 'application/octet-stream',
  base64: btoa(Array.from(new Uint8Array(await f.arrayBuffer()), (b) => String.fromCharCode(b)).join('')),
});

export function ClaimsPage() {
  const qc = useQueryClient();
  const [params, set] = useUrlState();
  const [search, setSearch] = useSearchBox(params, set);
  const state = params.get('state') ?? 'action';
  const page = Number(params.get('page') ?? 1);
  const selected = params.get('c');
  const filter: Record<string, string> = { state };
  for (const k of ['account', 'q']) if (params.get(k)) filter[k] = params.get(k)!;
  const qs = new URLSearchParams({ ...filter, page: String(page) });
  const list = useQuery({
    queryKey: ['claims', qs.toString()],
    queryFn: () => api<ListPage>(`/channels/mercadolivre/claims?${qs}`),
    placeholderData: keepPreviousData, refetchInterval: 60_000,
  });
  const orderId = params.get('order');
  const open = useMutation({
    mutationFn: (id: string) => api<{ ids: string[] }>('/channels/mercadolivre/claims/open', { method: 'POST', json: { orderId: id } }),
    onSuccess: (r) => set({ order: null, c: r.ids[0] ?? null, state: 'all' }),
  });
  useEffect(() => { if (orderId && !open.isPending) open.mutate(orderId); }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps
  const sync = useMutation({
    mutationFn: () => api('/channels/mercadolivre/claims/sync', { method: 'POST', json: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claims'] }),
  });
  const d = list.data;
  const c = d?.counts;

  return (
    <>
      <Head title="Reclamações">
        <p>Reclamações, devoluções e trocas do Mercado Livre. As que precisam de ação sua ficam no topo, pelo prazo. Com o incentivo do ML, resolver bem nas primeiras 48 horas não afeta a reputação.</p>
      </Head>
      <div className="stack-y">
        {open.error && <ErrorBox error={open.error} />}
        {open.isSuccess && open.data.ids.length === 0 && <div className="notice small">Este pedido não tem reclamação no Mercado Livre.</div>}
        {list.error ? <ErrorBox error={list.error} /> : !d || !c ? <Loading /> : (
          <>
            <div className="toolbar">
              <div className="seg" role="group" aria-label="Situação">
                {([['action', `Precisa de ação (${int(c.action)})`], ['open', `Abertas (${int(c.open)})`], ['dispute', `Em mediação (${int(c.dispute)})`], ['returns', `Devoluções (${int(c.returns)})`], ['closed', 'Encerradas'], ['all', 'Todas']] as const).map(([v, l]) => (
                  <button key={v} type="button" aria-pressed={state === v} onClick={() => set({ state: v === 'action' ? null : v, page: null })}>{l}</button>
                ))}
              </div>
              {c.urgent > 0 && <Tag tone="err">{int(c.urgent)} com prazo em menos de 24 h</Tag>}
              {c.affects > 0 && <Tag tone="warn">{int(c.affects)} afetando a reputação</Tag>}
              <label className="search">
                <Search size={16} />
                <span className="sr-only">Buscar</span>
                <input className="input" type="search" placeholder="Reclamação, pedido, comprador ou produto" value={search} onChange={(e) => setSearch(e.target.value)} />
              </label>
              <span className="grow" />
              <button type="button" className="btn small" disabled={sync.isPending} onClick={() => sync.mutate()}><RefreshCw size={14} /> {sync.isPending ? 'Lendo…' : 'Ler agora'}</button>
            </div>
            {sync.error && <ErrorBox error={sync.error} />}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 380px) 1fr', gap: 12, alignItems: 'start' }}>
              <div className="panel flush" style={{ opacity: list.isPlaceholderData ? 0.55 : 1 }}>
                {d.rows.length === 0 ? <div className="empty"><b>{state === 'action' ? 'Nenhuma reclamação esperando você' : 'Nada por aqui'}</b>As reclamações novas chegam sozinhas.</div> : d.rows.map((r) => {
                  const late = urgent(r.due);
                  return (
                    <button key={r.id} type="button" onClick={() => set({ c: r.id })}
                      style={{ display: 'grid', gap: 2, width: '100%', textAlign: 'left', padding: '10px 12px', border: 0, borderBottom: '1px solid var(--line)', cursor: 'pointer', font: 'inherit', color: 'inherit',
                        background: selected === r.id ? 'var(--surface-2)' : 'transparent', borderLeft: r.due ? `3px solid ${late ? 'var(--err)' : 'var(--warn)'}` : '3px solid transparent' }}>
                      <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <b style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.order?.buyer ?? `Reclamação ${r.externalId}`}</b>
                        {r.stage === 'dispute' && r.status === 'opened' && <Gavel size={13} aria-label="Em mediação" />}
                        {r.affectsReputation === 'affected' && r.status === 'opened' && <ShieldAlert size={13} color="var(--warn)" aria-label="Afeta a reputação" />}
                        {r.unread > 0 && <Tag tone="info">{r.unread}</Tag>}
                        <span className="small muted">{ago(r.openedAt)}</span>
                      </span>
                      <span className="small">
                        {r.typeLabel}{r.reasonGroup ? ` · ${r.reasonGroup}` : ''}
                      </span>
                      <span className="small muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.problem ?? r.reasonDetail ?? r.order?.items ?? `Pedido ${r.order?.externalId ?? '—'}`}</span>
                      {r.status === 'closed'
                        ? <span className="small muted">Encerrada{r.resolution ? `: ${r.resolution.label}` : ''}</span>
                        : r.due ? <span className="small" style={{ color: late ? 'var(--err)' : 'var(--warn)' }}>{WHO[r.responsible ?? ''] ?? 'Ação pendente'} · {until(r.due)}</span>
                        : <span className="small muted">{WHO[r.responsible ?? ''] ?? r.stageLabel}{r.returnStatusLabel ? ` · devolução: ${r.returnStatusLabel}` : ''}</span>}
                    </button>
                  );
                })}
                <Pager total={d.total} page={page} pageSize={d.pageSize} onPage={(p) => set({ page: p > 1 ? String(p) : null })} />
              </div>
              {selected ? <ClaimView key={selected} id={selected} onClose={() => set({ c: null })} />
                : <div className="panel"><div className="empty"><b>Escolha uma reclamação</b>Ou abra pelo pedido (botão Reclamações no detalhe do pedido).</div></div>}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Section({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section style={{ display: 'grid', gap: 6, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
      <h3 style={{ margin: 0, fontSize: 14, display: 'flex', gap: 6, alignItems: 'center' }}>{icon}{title}</h3>
      {children}
    </section>
  );
}

function ClaimView({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const t = useQuery({ queryKey: ['claim', id], queryFn: () => api<Detail>(`/channels/mercadolivre/claims/${id}`), refetchInterval: 60_000 });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['claim', id] }); void qc.invalidateQueries({ queryKey: ['claims'] }); };
  const [form, setForm] = useState<ActionKey | null>(null);
  if (t.isPending) return <div className="panel"><Loading /></div>;
  if (t.error || !t.data) return <div className="panel"><ErrorBox error={t.error} /></div>;
  const c = t.data;
  const o = c.order;
  const open = c.status === 'opened';

  return (
    <div className="panel" style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.4 }}>
          <b>{c.typeLabel}{c.reasonGroup ? ` · ${c.reasonGroup}` : ''}</b>
          <span className="small muted">{o?.buyer ?? '—'} · {o?.items ?? '—'}</span>
          <span className="small muted">
            Reclamação <span className="code">{c.externalId}</span>
            {o && <> · Pedido <Link to={`/pedidos?q=${o.externalId}&period=all`} className="code">{o.externalId}</Link> · {brl(o.total)}{o.shipmentStatus ? ` · ${SHIP[o.shipmentStatus] ?? o.shipmentStatus}` : ''}</>}
            {c.openedAt && ` · aberta ${dateTime(c.openedAt)}`}
          </span>
        </div>
        {open ? <Tag tone={c.stage === 'dispute' ? 'warn' : 'info'}>{c.stageLabel || 'Aberta'}</Tag> : <Tag tone="off">Encerrada</Tag>}
        <button type="button" className="icon-btn" aria-label="Fechar reclamação" onClick={onClose}><X size={16} /></button>
      </div>

      {c.syncError && <div className="notice warn small">Não foi possível atualizar agora: {c.syncError}</div>}

      <div style={{ display: 'grid', gap: 4, padding: 10, borderRadius: 8, background: 'var(--surface-2)' }}>
        {c.title && <b>{c.title}</b>}
        {c.description && <span className="small">{c.description}</span>}
        {(c.problem || c.reasonDetail) && <span className="small"><b>Problema:</b> {c.problem ?? c.reasonDetail}</span>}
        {open && (
          <span className="small" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {c.responsible && <b>{WHO[c.responsible] ?? c.responsible}</b>}
            {c.due && <Tag tone={urgent(c.due) ? 'err' : 'warn'}>{until(c.due)} · até {dateTime(c.due)}</Tag>}
          </span>
        )}
        {!open && c.resolution && (
          <span className="small"><b>Resultado:</b> {c.resolution.label}{c.resolution.benefited.length ? ` · a favor do ${c.resolution.benefited.map((b) => ROLE[b] ?? b).join(' e ')}` : ''}{c.resolution.coverage ? ' · com cobertura do Mercado Livre' : ''}</span>
        )}
      </div>

      {open && c.affectsReputation === 'affected' && (
        <div className="notice warn small"><ShieldAlert size={13} /> <b>Afeta a reputação.</b>{' '}
          {c.hasIncentive ? <>Se resolver bem {c.reputationDue ? `até ${dateTime(c.reputationDue)}` : 'nas primeiras 48 horas'}, não afeta.</> : 'O Mercado Livre não garante que resolver a tempo evite o impacto.'}
        </div>
      )}
      {open && c.affectsReputation === 'not_affected' && <div className="small muted">Não afeta a reputação.</div>}

      {c.expected.length > 0 && (
        <Section title="O que cada lado propôs">
          {c.expected.map((e, i) => (
            <div key={i} className="small" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <b>{e.who === 'complainant' ? 'Comprador' : e.who === 'respondent' ? 'Você' : 'Mediador'}:</b> {e.label}
              {e.details.percentage && ` (${Number(e.details.percentage)}%)`}
              <Tag tone={e.status === 'accepted' ? 'ok' : e.status === 'rejected' ? 'off' : 'warn'}>{e.status === 'accepted' ? 'aceita' : e.status === 'rejected' ? 'recusada' : 'pendente'}</Tag>
            </div>
          ))}
        </Section>
      )}

      {c.returnInfo && (
        <Section title="Devolução" icon={<Package size={14} />}>
          <div className="small" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <Tag tone={c.returnInfo.status === 'delivered' ? 'ok' : ['failed', 'expired', 'not_delivered'].includes(c.returnInfo.status) ? 'err' : 'info'}>{c.returnInfo.statusLabel}</Tag>
            {c.returnInfo.moneyLabel && <span>{c.returnInfo.moneyLabel}</span>}
            {c.returnInfo.refundAt && c.returnInfo.refundAt !== 'n/a' && <span className="muted">· reembolso ao comprador {c.returnInfo.refundAt === 'shipped' ? 'quando despachar' : '3 dias após você receber'}</span>}
            {c.returnInfo.cost != null && <span className="muted">· custo do frete da devolução para você: {brl(c.returnInfo.cost)}</span>}
          </div>
          {c.returnInfo.shipments.map((s) => (
            <div key={s.id} className="small muted">
              Envio {s.id} ({s.destination === 'warehouse' ? 'para o depósito do ML' : s.destination === 'seller_address' ? 'para a loja' : s.destination ?? '—'}): {SHIP[s.status] ?? s.status}{s.tracking ? ` · rastreio ${s.tracking}` : ''}
            </div>
          ))}
          {c.returnInfo.reviews.map((r, i) => (
            <div key={i} className="small">Revisão {r.method === 'triage' ? 'na triagem do ML' : 'da loja'}: {r.status === 'success' ? 'produto ok' : r.status === 'failed' ? 'produto com problema' : '—'}{r.product_condition ? ` · ${r.product_condition === 'saleable' ? 'pode ser vendido' : r.product_condition === 'unsaleable' ? 'não pode ser vendido' : r.product_condition === 'discard' ? 'descartado' : r.product_condition === 'missing' ? 'não chegou' : r.product_condition}` : ''}</div>
          ))}
        </Section>
      )}

      {c.change && (
        <Section title={c.change.type === 'replace' ? 'Troca pelo mesmo produto' : 'Troca'} icon={<Repeat size={14} />}>
          <div className="small">
            <Tag tone={c.change.status === 'changed' ? 'ok' : /fail/.test(c.change.status) ? 'err' : 'info'}>{c.change.statusLabel}</Tag>
            {c.change.newOrders.length > 0 && <span className="muted"> · novo pedido {c.change.newOrders.join(', ')}</span>}
            {c.change.estimated?.to && <span className="muted"> · previsão até {dateTime(c.change.estimated.to)}</span>}
          </div>
        </Section>
      )}

      {c.evidences.length > 0 && (
        <Section title="Provas de envio informadas">
          {c.evidences.map((e, i) => (
            <div key={i} className="small">
              {e.type === 'handling_shipping_evidence' ? `Data prevista de envio: ${e.handlingDate ? dateTime(e.handlingDate) : '—'}`
                : `${e.company ?? e.method ?? 'Envio'}${e.tracking ? ` · rastreio ${e.tracking}` : ''}${e.dateShipped ? ` · enviado ${dateTime(e.dateShipped)}` : ''}`}
              {e.attachments.map((a) => <a key={a.filename} className="small" style={{ marginLeft: 6 }} href={`/api/v1/channels/mercadolivre/claims/${id}/attachments/${encodeURIComponent(a.filename)}`} target="_blank" rel="noreferrer"><Paperclip size={11} /> {a.name}</a>)}
            </div>
          ))}
        </Section>
      )}

      {open && (
        <Section title="O que você pode fazer agora">
          {c.available.length === 0 ? <div className="small muted">O Mercado Livre não libera nenhuma ação para a loja agora{c.receivers.length ? ' além de mensagens' : ''}.</div> : (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {c.available.map((a) => (
                <button key={a.action} type="button" className={`btn small${form === a.action ? ' primary' : ''}`} disabled={!a.allowed}
                  title={a.allowed ? ACTION_TEXT[a.action].hint : 'Devolver dinheiro exige perfil admin'} onClick={() => setForm(form === a.action ? null : a.action)}>
                  {ACTION_TEXT[a.action].label}{!a.allowed ? ' (admin)' : ''}
                </button>
              ))}
            </div>
          )}
          {form && <ActionForm key={form} id={id} action={form} claim={c} onDone={() => { setForm(null); refresh(); }} />}
        </Section>
      )}

      <Section title={`Mensagens${c.messages.length ? ` (${c.messages.length})` : ''}`}>
        <div style={{ display: 'grid', gap: 8, maxHeight: 420, overflow: 'auto' }}>
          {c.messages.length === 0 && <div className="small muted">Nenhuma mensagem nesta reclamação.</div>}
          {c.messages.map((m) => {
            const mine = m.from === 'respondent';
            return (
              <div key={m.id} style={{ justifySelf: mine ? 'end' : 'start', maxWidth: '80%', display: 'grid', gap: 2 }}>
                <div style={{ background: mine ? 'color-mix(in srgb, var(--accent, #2f6bff) 14%, var(--surface))' : m.from === 'mediator' ? 'color-mix(in srgb, var(--warn) 14%, var(--surface))' : 'var(--surface-2)', borderRadius: 10, padding: '8px 11px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {m.text || <span className="muted">(sem texto)</span>}
                  {m.attachments.length > 0 && (
                    <div style={{ display: 'grid', gap: 2, marginTop: 4 }}>
                      {m.attachments.map((a) => <a key={a.filename} className="small" href={`/api/v1/channels/mercadolivre/claims/${id}/attachments/${encodeURIComponent(a.filename)}`} target="_blank" rel="noreferrer"><Paperclip size={11} /> {a.name}</a>)}
                    </div>
                  )}
                </div>
                <span className="small muted" style={{ justifySelf: mine ? 'end' : 'start' }}>
                  {mine ? (m.sentBy ?? 'você (pelo ML)') : ROLE[m.from] ?? m.from}{m.to ? ` → ${ROLE[m.to] ?? m.to}` : ''} · {dateTime(m.sentAt)}
                  {m.moderation && ` · moderada pelo ML${m.moderation.reason === 'OUT_OF_PLACE_LANGUAGE' ? ': linguagem inadequada' : ''}`}
                </span>
              </div>
            );
          })}
        </div>
        {open && <Composer id={id} receivers={c.receivers} stage={c.stage} onSent={refresh} />}
      </Section>
    </div>
  );
}

function FilePicker({ files, setFiles }: { files: File[]; setFiles: (f: File[]) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <input ref={ref} type="file" multiple hidden accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
        onChange={(e) => { setFiles([...files, ...Array.from(e.target.files ?? [])].slice(0, 5)); e.target.value = ''; }} />
      <button type="button" className="btn small ghost" onClick={() => ref.current?.click()}><Paperclip size={14} /> Anexar</button>
      {files.map((f, i) => (
        <Tag key={i} tone={f.size > 5 * 1024 * 1024 ? 'err' : 'off'}>{f.name}{f.size > 5 * 1024 * 1024 ? ' (mais de 5 MB)' : ''} <button type="button" className="icon-btn" style={{ width: 16, height: 16 }} aria-label="Tirar" onClick={() => setFiles(files.filter((_, j) => j !== i))}><X size={11} /></button></Tag>
      ))}
    </>
  );
}

function Composer({ id, receivers, stage, onSent }: { id: string; receivers: ('complainant' | 'mediator')[]; stage: string; onSent: () => void }) {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [to, setTo] = useState<'complainant' | 'mediator'>(stage === 'dispute' && receivers.includes('mediator') ? 'mediator' : receivers[0] ?? 'complainant');
  const send = useMutation({
    mutationFn: async () => api(`/channels/mercadolivre/claims/${id}/message`, { method: 'POST', json: { text, receiver: to, attachments: await Promise.all(files.map(toBase64)) } }),
    onSuccess: () => { setText(''); setFiles([]); onSent(); },
  });
  if (!receivers.length) return <div className="small muted">O Mercado Livre não libera mensagem da loja nesta etapa.</div>;
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {receivers.length > 1 && (
        <div className="seg" role="group" aria-label="Para">
          {receivers.map((r) => <button key={r} type="button" aria-pressed={to === r} onClick={() => setTo(r)}>Para o {ROLE[r]}</button>)}
        </div>
      )}
      <textarea className="input" rows={3} maxLength={2000} placeholder={to === 'mediator' ? 'Mensagem para o mediador do Mercado Livre…' : 'Mensagem para o comprador…'}
        value={text} onChange={(e) => setText(e.target.value)} style={{ resize: 'vertical', minHeight: 70, height: 'auto', padding: '8px 10px' }} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <FilePicker files={files} setFiles={setFiles} />
        <span className="small muted">JPG, PNG ou PDF até 5 MB</span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn primary small" disabled={!text.trim() || send.isPending || files.some((f) => f.size > 5 * 1024 * 1024)} onClick={() => send.mutate()}><Send size={14} /> {send.isPending ? 'Enviando…' : 'Enviar'}</button>
      </div>
      {send.error && <ErrorBox error={send.error} />}
    </div>
  );
}

function ActionForm({ id, action, claim, onDone }: { id: string; action: ActionKey; claim: Detail; onDone: () => void }) {
  const [v, setV] = useState<Record<string, string>>({ method: 'mail' });
  const [files, setFiles] = useState<File[]>([]);
  const offers = useQuery({ queryKey: ['claim-offers', id], enabled: action === 'partial_refund', queryFn: () => api<{ offers: { amount: number; percentage: number }[]; recommendations: { percentage: number; type?: string }[] }>(`/channels/mercadolivre/claims/${id}/partial-offers`) });
  const reasons = useQuery({ queryKey: ['claim-return-reasons', id], enabled: action === 'return_review_fail', queryFn: () => api<{ reasons: { id: string; detail: string; apply: string[] }[] }>(`/channels/mercadolivre/claims/${id}/return-reasons`) });
  const run = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { action, attachments: await Promise.all(files.map(toBase64)) };
      for (const [k, x] of Object.entries(v)) if (x !== '') body[k] = k === 'percentage' ? Number(x) : x;
      if (action !== 'shipping_evidence') delete body.method;
      return api(`/channels/mercadolivre/claims/${id}/action`, { method: 'POST', json: body });
    },
    onSuccess: onDone,
  });
  const field = (k: string, label: string, type = 'text') => (
    <label className="small" style={{ display: 'grid', gap: 2 }}>{label}<input className="input" type={type} value={v[k] ?? ''} onChange={(e) => setV({ ...v, [k]: e.target.value })} /></label>
  );
  const info = ACTION_TEXT[action];
  const simple = ['refund', 'allow_return', 'allow_replace', 'open_dispute', 'return_review_ok'].includes(action);
  const confirmText = action === 'refund' ? `Devolver ${brl(claim.order?.total ?? null)} ao comprador? Isso encerra a reclamação.` : null;

  return (
    <div style={{ display: 'grid', gap: 8, padding: 10, border: '1px solid var(--line)', borderRadius: 8 }}>
      <div className="small">{info.hint}</div>
      {action === 'refund' && <div className="notice warn small"><AlertTriangle size={13} /> O dinheiro sai da sua conta no Mercado Pago na hora.</div>}

      {action === 'partial_refund' && (offers.isPending ? <Loading /> : offers.error ? <ErrorBox error={offers.error} /> : (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {offers.data!.offers.map((x) => (
            <button key={x.percentage} type="button" className={`btn small${v.percentage === String(x.percentage) ? ' primary' : ''}`} onClick={() => setV({ ...v, percentage: String(x.percentage) })}>
              {x.percentage}% · {brl(x.amount)}
            </button>
          ))}
          {offers.data!.recommendations.map((r, i) => <span key={i} className="small muted">O ML sugere até {r.percentage}%.</span>)}
        </div>
      ))}

      {action === 'shipping_promise' && field('handlingDate', 'Data prevista de envio', 'date')}

      {action === 'shipping_evidence' && (
        <div style={{ display: 'grid', gap: 6, gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
          <label className="small" style={{ display: 'grid', gap: 2 }}>Como foi enviado
            <select className="input" value={v.method} onChange={(e) => setV({ ...v, method: e.target.value })}>
              <option value="mail">Correio</option><option value="entrusted">Transportadora</option><option value="personal_delivery">Em mãos</option><option value="email">Por e-mail</option>
            </select>
          </label>
          {(v.method === 'mail' || v.method === 'entrusted') && field('company', 'Transportadora')}
          {v.method !== 'personal_delivery' && field('dateShipped', 'Data de envio', 'date')}
          {(v.method === 'mail' || v.method === 'entrusted') && field('tracking', 'Código de rastreio (opcional)')}
          {v.method === 'entrusted' && <>{field('agency', 'Agência de destino')}{field('receiverName', 'Quem recebe')}</>}
          {v.method === 'email' && field('receiverEmail', 'E-mail do destinatário', 'email')}
          {v.method === 'personal_delivery' && field('dateDelivered', 'Data da entrega', 'date')}
        </div>
      )}

      {action === 'return_review_fail' && (reasons.isPending ? <Loading /> : reasons.error ? <ErrorBox error={reasons.error} /> : (
        <>
          <label className="small" style={{ display: 'grid', gap: 2 }}>O que houve
            <select className="input" value={v.reason ?? ''} onChange={(e) => setV({ ...v, reason: e.target.value })}>
              <option value="">Escolha…</option>
              {reasons.data!.reasons.map((r) => <option key={r.id} value={r.id}>{r.detail}</option>)}
            </select>
          </label>
          <label className="small" style={{ display: 'grid', gap: 2 }}>Explique
            <textarea className="input" rows={2} maxLength={2000} value={v.message ?? ''} onChange={(e) => setV({ ...v, message: e.target.value })} style={{ height: 'auto', padding: '8px 10px' }} />
          </label>
          {['SRF2', 'SRF4'].includes(v.reason ?? '') && <div className="small" style={{ color: 'var(--warn)' }}>Para produto danificado ou diferente, o Mercado Livre exige foto.</div>}
        </>
      ))}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {(action === 'shipping_evidence' || action === 'return_review_fail') && <FilePicker files={files} setFiles={setFiles} />}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn primary small" disabled={run.isPending || (action === 'partial_refund' && !v.percentage)}
          onClick={() => {
            const ask = action === 'partial_refund' ? `Oferecer ${v.percentage}% de reembolso ao comprador? Se ele aceitar, o valor sai da sua conta e a reclamação encerra.` : simple ? confirmText ?? `${info.label}?` : null;
            if (!ask || window.confirm(ask)) run.mutate();
          }}>
          {run.isPending ? 'Enviando…' : simple ? info.label : 'Enviar ao Mercado Livre'}
        </button>
      </div>
      {run.error && <ErrorBox error={run.error} />}
    </div>
  );
}
